import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { createI18n } from 'vue-i18n';
import { watch } from 'vue';
import { config } from './config/config';
import { useSettingsStore } from './stores/settingsStore';

import App from './App.vue';
import router from './router';
import en from './i18n/en.json';
import fr from './i18n/fr.json';

import './style.css';

const messages = { en, fr };

const app = createApp(App);
const pinia = createPinia();
app.use(pinia);

// #525 — one source of truth for the UI language: the settings store, which
// resolves the browser language once. It seeds vue-i18n here and every
// later change (any LocaleToggle) is mirrored below; nothing else writes
// vue-i18n. A language listed in config without a messages file would show
// its code on the button and English text — say so loudly at startup.
const settingsStore = useSettingsStore();
const unlisted = config.locale.supported.filter((code) => !(code in messages));
if (unlisted.length > 0) {
  console.error(JSON.stringify({ event: 'locale_without_messages', locales: unlisted }));
}

function applyDocumentLanguage(code: string): void {
  document.documentElement.lang = code; // screen readers and translate bars read this, not vue-i18n
}
applyDocumentLanguage(settingsStore.locale);

const i18n = createI18n({
  legacy: false,
  locale: settingsStore.locale as keyof typeof messages,
  fallbackLocale: config.locale.fallback,
  messages
});

app.use(router);
app.use(i18n);

watch(
  () => settingsStore.locale,
  (next) => {
    i18n.global.locale.value = next as typeof i18n.global.locale.value;
    applyDocumentLanguage(next);
  }
);

app.mount('#app');
